import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Order } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useTradeLock } from "@/hooks/useTradeLock";
import { useGetOpenTasks } from "@/services/Tasks";
import { postProcessOrder } from "@/services/Orders";
import { postProcessOrder as postApproveOrder, useGetContractById } from "@/services/Contracts";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getContractParties } from "@/utils/Contracts";
import { notifications } from "@mantine/notifications";
import { Grid } from "@mantine/core";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import "@mantine/dates/styles.css";
import {
  ProcessOrderFormInput,
  ProcessOrderValidationSchema,
} from "@/components/pages/Tasks/validation/processOrderValidation";
import TaskTypeEnum from "@/constants/enums/TaskTypeEnum";
import Accordion from "@/components/organisms/AccordionDrawer";
import CTA from "@/components/atoms/CTA";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import dayjs from "dayjs";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";

interface AccordionOrderActionsProps {
  order: Order;
}

const AccordionOrderActions = ({ order }: AccordionOrderActionsProps) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const { guardTradeLock } = useTradeLock();
  const queryClient = useQueryClient();
  const close = useOrdersDrawerStore((s) => s.close);
  const { data: tasks } = useGetOpenTasks(currentCompany?.companyId as number, undefined, {
    enabled: !!currentCompany?.companyId,
  });
  // The order only knows its counterparty's id; the contract carries whether that company is locked. Same query
  // as AccordionFinalAmount, so the drawer shares the one fetch. The backend doesn't check the lock when an order
  // is processed or approved, so the button stays blocked until the contract is here — including when the fetch
  // fails — rather than letting the guard pass unseen.
  const {
    data: contract,
    isLoading: isLoadingContract,
    isError: isContractError,
  } = useGetContractById(order.contractId as number, {
    enabled: !!order.contractId,
  });
  const isContractUnavailable = !!order.contractId && !contract;

  const actionableTask = useMemo(
    () =>
      tasks?.find(
        (task) =>
          task.connected === order.id &&
          (task.type === TaskTypeEnum.APPROVE_ORDER || task.type === TaskTypeEnum.PROCESS_ORDER)
      ),
    [tasks, order.id]
  );

  const defaults = useMemo<ProcessOrderFormInput>(
    () => ({
      startDate: order.deliveryStart ? dayjs(order.deliveryStart).toDate() : dayjs(order.created).toDate(),
      endDate: order.deliveryEnd ? dayjs(order.deliveryEnd).toDate() : dayjs().toDate(),
    }),
    [order.deliveryStart, order.deliveryEnd, order.created]
  );

  const { handleSubmit, control } = useForm<ProcessOrderFormInput>({
    resolver: zodResolver(ProcessOrderValidationSchema),
    mode: "onBlur",
    values: defaults,
  });

  const { mutate: approve, isPending: isApproving } = useMutation({
    mutationFn: async (data: ProcessOrderFormInput) => {
      if (actionableTask?.type === TaskTypeEnum.APPROVE_ORDER) {
        return postApproveOrder(order.id);
      }
      return postProcessOrder({
        orderId: order.id,
        taskId: actionableTask?.id,
        startDate: data.startDate,
        endDate: data.endDate,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getOpenTasks", currentCompany?.companyId] }),
        queryClient.invalidateQueries({ queryKey: ["getOrdersForCompany", currentCompany?.companyId] }),
        queryClient.invalidateQueries({ queryKey: ["order", order.id] }),
      ]);
      close();
      notifications.show(SuccessNotificationObj(t("notifications:generic.saveSuccess"), ""));
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.saveError"), ""));
    },
  });

  // Guards both branches of the mutation above — processing an order and approving one.
  const processOrder = useMemo(
    () => guardTradeLock(approve, () => getContractParties(contract)),
    [guardTradeLock, approve, contract]
  );

  const isProcessOrder = order.status === "AWAITING_PROCESSING"; //
  if (!isProcessOrder) return null; // TODO remove this line when you want to handle approve order task type
  return (
    <Accordion title={t("orders:actions")} initiallyOpen>
      <form onSubmit={handleSubmit((data) => processOrder(data))}>
        <Grid p={20}>
          <Grid.Col span={12}>
            <Text size="sm">{t("enumerables:orderStatus." + order.status)}</Text>
          </Grid.Col>

          {isProcessOrder && (
            <>
              <Grid.Col span={12}>
                <Text size="sm" strong>
                  {t("orders:suggestedDeliveryWindow")}
                </Text>
                <Text size="xs" color="secondary">
                  {t("orders:suggestedDeliveryWindowDescription")}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <DatePickerInput
                      label={t("orders:from")}
                      value={value ?? null}
                      onChange={(date) => onChange(date ?? undefined)}
                      error={error?.message}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <DatePickerInput
                      label={t("orders:to")}
                      value={value ?? null}
                      onChange={(date) => onChange(date ?? undefined)}
                      error={error?.message}
                    />
                  )}
                />
              </Grid.Col>
            </>
          )}

          <Grid.Col span={12}>
            <Group justify="flex-end">
              {isContractUnavailable && isContractError && (
                <Text size="xs" color="error" data-testid="order-actions-contract-error">
                  {t("notifications:contracts.fetchError")}
                </Text>
              )}
              <CTA
                type="submit"
                intent="primary"
                loading={isApproving || isLoadingContract}
                disabled={isContractUnavailable}
                data-testid="order-actions-approve"
              >
                {t("actions:approve")}
              </CTA>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Accordion>
  );
};

export default AccordionOrderActions;
